/**
 * SessionProvider Context
 * Provides session management functionality throughout the app
 */

"use client";

import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import useSessionManagement from "@/hooks/useSessionManagement";
import { UserSession } from "@/types/session";

interface SessionContextType {
  activeSessions: UserSession[];
  sharingDetected: boolean;
  anomalies: string[];
  loading: boolean;
  error: string | null;
  isMonitoring: boolean;
  lastChecked: string | null;
  fetchActiveSessions: () => Promise<void>;
  checkAccountSharing: () => Promise<any>;
  logoutFromDevice: (sessionId: string | number) => Promise<any>;
  logoutAllOtherDevices: () => Promise<any>;
  updateActivity: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export interface SessionProviderProps {
  children: ReactNode;
  autoCheck?: boolean;
  checkInterval?: number;
  enableActivityTracking?: boolean;
}

/**
 * SessionProvider Component
 * Wraps the app to provide session management context
 */
export const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
  autoCheck = false,
  checkInterval = 5 * 60 * 1000,
  enableActivityTracking = false,
}) => {
  const sessionManagement = useSessionManagement({
    autoCheck,
    checkInterval,
    enableActivityTracking,
  });

  return (
    <SessionContext.Provider value={sessionManagement}>
      {children}
    </SessionContext.Provider>
  );
};

/**
 * Hook to use SessionContext
 */
export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return context;
};

export default SessionContext;
